import React from 'react';
import type { QualityMetrics } from '../types';
import { qualityScoreColor } from '../utils/helpers';
import { Star, TrendingUp, ArrowUpRight } from 'lucide-react';

interface Props {
  quality: QualityMetrics;
}

const DIMENSION_LABELS: Record<string, string> = {
  pushPullBalance: 'Balans Push/Pull',
  movementCoverage: 'Pokrycie ruchów',
  fatigueManagement: 'Zarządzanie zmęczeniem',
  volumeDistribution: 'Rozkład objętości',
  progressionLogic: 'Logika progresji',
};

const CATEGORY_ICON: Record<string, string> = {
  balance: '⚖️',
  coverage: '🎯',
  fatigue: '💤',
  volume: '📊',
  progression: '📈',
};

export default function QualityScoreCard({ quality }: Props) {
  const color = qualityScoreColor(quality.overallScore);
  const grade =
    quality.overallScore >= 90
      ? 'A+'
      : quality.overallScore >= 80
      ? 'A'
      : quality.overallScore >= 70
      ? 'B'
      : quality.overallScore >= 60
      ? 'C'
      : quality.overallScore >= 50
      ? 'D'
      : 'F';

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
      <h3 className="text-sm font-bold mb-1 flex items-center gap-1.5">
        <Star size={14} className="text-amber-400" />
        Ocena jakości
      </h3>
      <p className="text-[10px] text-slate-400 mb-3">
        Jakość programu w pięciu wymiarach
      </p>

      {/* ── Score header ────────────────── */}
      <div className="flex items-center gap-4 mb-4">
        <div
          className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-black"
          style={{ backgroundColor: color + '15', color }}
        >
          {grade}
        </div>
        <div>
          <div className="text-3xl font-bold" style={{ color }}>
            {quality.overallScore}
            <span className="text-sm text-slate-400 font-normal">/100</span>
          </div>
          <span className="text-[10px] text-slate-400">
            {quality.overallScore >= 80
              ? 'Doskonale zaprojektowany program'
              : quality.overallScore >= 60
              ? 'Dobry - możliwe ulepszenia'
              : 'Wymaga znaczących poprawek'}
          </span>
        </div>
      </div>

      {/* ── Dimension breakdown ─────────── */}
      <div className="space-y-2 mb-4">
        {Object.entries(quality.breakdown).map(([key, score]) => (
          <div key={key}>
            <div className="flex justify-between text-xs mb-0.5">
              <span className="text-slate-600 dark:text-slate-300">
                {DIMENSION_LABELS[key] || key}
              </span>
              <span
                className="font-bold font-mono"
                style={{ color: qualityScoreColor(score) }}
              >
                {score}
              </span>
            </div>
            <div className="h-1.5 rounded bg-slate-100 dark:bg-slate-800 overflow-hidden">
              <div
                className="h-full rounded transition-all duration-500"
                style={{
                  width: `${score}%`,
                  backgroundColor: qualityScoreColor(score),
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* ── Improvement suggestions ─────── */}
      {quality.suggestions.length > 0 && (
        <div>
          <h4 className="text-[10px] font-semibold text-slate-400 uppercase mb-2 flex items-center gap-1">
            <TrendingUp size={10} />
            Sugestie ulepszeń
          </h4>
          <div className="space-y-1.5">
            {quality.suggestions.slice(0, 6).map((s, i) => (
              <div
                key={i}
                className={`text-[11px] p-2 rounded-lg flex items-start gap-2 ${
                  s.priority === 'high'
                    ? 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300'
                    : s.priority === 'medium'
                    ? 'bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                }`}
              >
                <span>{CATEGORY_ICON[s.category] || '💡'}</span>
                <span>{s.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
